import _ from "lodash";
import {
  FollowUpEntityType,
  FollowUpUniqueCol,
  LastAction,
} from "us.collection.followup/constants";
import { FollowUpAction } from "us.common/components/FollowUps/Constants";
import { SavedFilterStatus, ClaimOwnerShipForFilter, FollowupUniqueNames } from "../Constants";
import { EntityType } from "us.common/constants";
import {
  IFollowUpItem,
  IFollowUpFilters,
} from "us.collection.followup/interfaces";

/**
 * @function
 * @description filter followup data according to last action
 * @param dataSource followup table data
 * @param lastAction last action
 * @returns
 */
export const filterFollowUpByLastAction = (
  dataSource: Array<Object>,
  lastAction: string
): Array<Object> => {
  switch (lastAction) {
    case LastAction.ALL:
      return dataSource;
    case LastAction.PENDING:
      return dataSource.filter(
        (followUp: any) => followUp?.Lastactionstatus === LastAction.PENDING
      );
    case LastAction.EXECUTED:
      return dataSource.filter(
        (followUp: any) =>
          followUp?.Lastactionstatus === LastAction.SUCCESS ||
          followUp?.Lastactionstatus === LastAction.EXECUTED
      );
    case LastAction.FAILED:
      return dataSource.filter(
        (followUp: any) =>
          followUp?.Lastactionstatus === LastAction.FAILED ||
          followUp?.Lastactionstatus === LastAction.PENDING_FAILED
      );
    case LastAction.OTHER:
      return dataSource.filter(
        (followUp: any) =>
          followUp?.Lastactionstatus === "" ||
          followUp?.Lastactionstatus === LastAction.OPEN ||
          followUp?.Lastactionstatus === LastAction.VIEW
      );
    default:
      return dataSource;
  }
};

/**
 * @description - Check whether the given id (case no, sub case no, debtor or creditor no) related result is viewed or not
 * @param {Array<string | number>} viewedList
 * @param {string | number} id
 */
export const isViewed = (
  viewedList: Array<string | number>,
  id: string | number
): Boolean => {
  try {
    if (viewedList.length > 0) {
      return viewedList.some((viewedId) => viewedId == id);
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};

/**
 * @function
 * @description get displayName
 * @param {Array<any>} followups -followup list
 * @param {string | number} followupId -followup Id that is in query params
 * @returns {string} followup name
 */
export const getFollowupName = (
  followups: Array<any>,
  followupId?: string | number
): string => {
  try {
    if (followups.length > 0 && followupId) {
      return (
        followups.find(
          (followup: any) => followup.followupId.toString() == followupId
        )?.followupDisplayName ?? ""
      );
    } else return "";
  } catch (error) {
    return "";
  }
};

/**
 * @description - Get find linked column with respect to the entityType
 * @param entityType - entityType
 * @returns  final array of possible columns to linked.
 */
export const getLinkedEntity = (entityType: string): string => {
  switch (entityType) {
    case FollowUpEntityType.CASE:
    case FollowUpEntityType.INVOICE:
      return FollowUpUniqueCol.EX_CASE_NO;
    case FollowUpEntityType.CREDITOR:
      return FollowUpUniqueCol.CREDITOR_NO;
    case FollowUpEntityType.DEBTOR:
      return FollowUpUniqueCol.AR_NO;
    case FollowUpEntityType.PAYMENT:
      return FollowUpUniqueCol.PAYMENT_ID;
    default:
      return "";
  }
};

/**
 * @description - Get conformation message for action
 * @param type - Action type
 * @returns
 */
export const getConfirmationMessage = (type: string): string => {
  switch (type) {
    case FollowUpAction.POSTPONE:
      return "US.COLLECTION.FOLLOWUP:MANUAL_FOLLOWUP.POSTPONE_CONFIRM";
    case FollowUpAction.DONE:
      return "US.COLLECTION.FOLLOWUP:MANUAL_FOLLOWUP.DONE_CONFIRM";
    case FollowUpAction.REMOVE:
      return "US.COLLECTION.FOLLOWUP:MANUAL_FOLLOWUP.REMOVE_CONFIRM";
    default:
      return "";
  }
};

/**
 * @description - Format and return selected filter data to store in redux state
 * @param {Array<IFollowUpItem & FollowUpFilter>} followUps - Followup list with filters
 * @param {IFollowUpFilters} previousFilterState - Last filtered data from redux store
 * @param {string | number} selectedFilterId - Selected filter id
 * @returns {IFollowUpFilters} Formatted data.
 */
export const getSavedFilters = (
  followUps: Array<IFollowUpItem>,
  previousFilterState: IFollowUpFilters,
  selectedFilterId?: string | number,
  caseHandlerData?: any
): IFollowUpFilters => {
  try {
    if (Array.isArray(followUps) && selectedFilterId) {
      const selectedFilter = _.find(followUps, {
        isFilter: true,
        filterId: Number(selectedFilterId),
      });

      if (selectedFilter) {
        const { status, caseType, claimOwnership, reasonIds, caseHandlers, createdUser } =
          selectedFilter ?? {};
        const statuses: any[] = status?.replace(/\s+/g, "")?.split(",");
        const reasons: any[] = reasonIds
          ?.replace(/\s+/g, "")
          ?.split(",")
          ?.filter((item: any) => !isNaN(item))
          .map(Number);
          let caseHandlerList: any[] = caseHandlers
          ?.replace(/\s+/g, "")
          ?.split(",")
          ?.filter((item: any) => item !== "" && !isNaN(item)) 
          .map(Number);

          if(Array.isArray(caseHandlerData) && caseHandlerData.length > 0){
            caseHandlerList = caseHandlerList.filter(
            (selectedHandler: any) =>
              caseHandlerData.some(
                (caseHandler: any) => caseHandler.id == selectedHandler
              )
          );
          }
        return {
          ...previousFilterState,
          caseStates: {
            open: statuses.includes(SavedFilterStatus.OPEN),
            sentence: statuses.includes(SavedFilterStatus.SENTENCE),
            nextDueDate: statuses.includes(SavedFilterStatus.NEXT_DUE_DATE),
            court: statuses.includes(SavedFilterStatus.COURT),
            close: statuses.includes(SavedFilterStatus.CLOSED),
          },
          entityType:
            caseType == EntityType.SUB_CASE?.toUpperCase()
              ? FollowUpEntityType.INVOICE
              : FollowUpEntityType.CASE,
          userOwnedClaims: claimOwnership == ClaimOwnerShipForFilter.MY,
          selectedReasons: reasons,
          selectedCaseHandlers: caseHandlerList,
          createdUser: createdUser
        };
      }
      return previousFilterState;
    } else {
      return previousFilterState;
    }
  } catch (error) {
    return previousFilterState;
  }
};

/**
 * @description - Returns the name of the selected filter
 * @param {Array<IFollowUpItem & FollowUpFilter>} followUps - Followup list with filters
 * @param  {string | number} selectedFilterId - Selected filter id
 * @returns {string} - The name of the selected filter
 */
export const getSelectedFilterName = (
  followUps: Array<IFollowUpItem>,
  selectedFilterId?: string | number
): string => {
  try {
    if (Array.isArray(followUps) && selectedFilterId) {
      const selectedFilter = _.find(followUps, {
        isFilter: true,
        filterId: Number(selectedFilterId),
      });
      if (selectedFilter) {
        return selectedFilter.filterName ? selectedFilter.filterName : "";
      } else {
        return "";
      }
    } else {
      return "";
    }
  } catch (error) {
    return "";
  }
};

/**
 * @description - Check whether the filter name is duplicating
 * @param {Array<IFollowUpItem & FollowUpFilter>} followups - Followup list with filters
 * @param {string} filterName - User entered filter name
 * @param {string} filterId - Selected filter
 * @param {boolean} isEdit - Wether the validation is for filter editing
 * @returns {boolean} Duplicating status
 */
export const isFilterNameDuplicating = (
  followups: Array<IFollowUpItem>,
  filterName: string,
  filterId?: string,
  isEdit?: boolean
): boolean => {
  try {
    if (Array.isArray(followups)) {
      let filters = followups?.filter((followup) => followup?.isFilter);
      filters =
        isEdit && filterId
          ? filters?.filter((filter) => filter?.filterId != filterId)
          : filters;

      return _.some(
        filters,
        (filter) =>
          filter?.filterName?.trim()?.toLowerCase() ==
          filterName?.trim()?.toLowerCase()
      );
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};

/**
 * @function
 * @description get followup name
 * @param {Array<any>} followups -followup list
 * @param {string | number} followupId -followup Id that is in query params
 * @returns {string} followup name
 */
export const getFollowupUniqueName = (
  followups: Array<any>,
  followupId?: string | number
): string => {
  try {
    if (followups.length > 0 && followupId) {
      return (
        followups.find(
          (followup: any) => followup.followupId.toString() == followupId
        )?.followupName ?? ""
      );
    } else return "";
  } catch (error) {
    return "";
  }
};

/**
 * @description - Check whether the followup is common followup
 * @param {Array<IFollowUpItem & FollowUpFilter>} followups - Followup list with filters
 * @param {string | number} followupId - selected followup Id
 * @returns {boolean} True if common followup
 */
export const isCommonFollowup = (
  followups: Array<IFollowUpItem>,
  followupId?: string | number
): boolean => {
  try {
    return (
      getFollowupUniqueName(followups, followupId)?.trim()?.toLowerCase() ===
      FollowupUniqueNames.COMMON_FOLLOWUP
    );
  } catch (error) {
    return false;
  }
};

/**
 * @description - Check whether the followup is address unknown followup
 * @param {Array<IFollowUpItem & FollowUpFilter>} followups - Followup list with filters
 * @param {string | number} followupId - selected followup Id
 * @returns {boolean} True if address unknown followup
 */
export const isAddressUnknownFollowup = (
  followups: Array<IFollowUpItem>,
  followupId?: string | number
): boolean => {
  try {
    return (
      getFollowupUniqueName(followups, followupId)?.trim()?.toLowerCase() ===
      FollowupUniqueNames.ADDRESS_UNKNOWN
    );
  } catch (error) {
    return false;
  }
};

/**
 * @description - Handle filter selection when deleting or adding a filter
 * @param filterAddRemoveInfo - Add remove filter information
 * @param history - history object
 * @param location location object
 */
export const manageFilterSelection = (
  filterAddRemoveInfo: any,
  history: any,
  location: any
) => {
  try {
    const { deleteCount, addedFilterId } = filterAddRemoveInfo;
    if (deleteCount > 0) {
      const pathSegments = _.compact(location.pathname.split("/"));
      pathSegments.pop();
      const newPath = "/" + pathSegments.join("/");
      history.replace(newPath);
    }

    if (addedFilterId > 0) {
      const pathSegments = location.pathname.split("/");
      if (pathSegments.length > 3) {
        pathSegments[pathSegments.length - 1] = addedFilterId;
      } else {
        pathSegments.push(addedFilterId?.toString());
      }
      const newPath = pathSegments.join("/");
      history.replace(newPath);
    }
  } catch (error) {}
};
